'use client'

import { useMemo, useState, useCallback, useEffect, useRef } from 'react'

import { isAxiosError } from 'axios'
import { useAppRouter } from '@/shared/lib/router'
import { toast } from 'sonner'

import { useQueryClient } from '@tanstack/react-query'

import type { LevelCosmeticEntry } from '../types'

import { Level, useActiveJourney } from '@/features/journeys'
import { fetchLevelAnswersExport } from '@/features/journeys/services/journey-answer-export.api'
import { generateJourneyAnswersPdf } from '@/features/journeys/services/journey-answer-export.pdf'
import { useJourneysStore } from '@/features/journeys/stores/useJourneysStore'
import { useLevelContext } from '@/features/level-play'
import { getLevelSummaryExitConfig } from '@/features/level-play/domain/getLevelSummaryExitConfig'
import {
  findFallbackNextLevelUid,
  resolveCompletionNextAction
} from '@/features/level-play/domain/resolveCompletionNextAction'
import { resolveNewlyUnlockedCosmetic } from '@/features/level-play/domain/resolveNewlyUnlockedCosmetic'
import {
  CosmeticsProfileQueryData,
  fetchUserCosmeticsProfile,
  userCosmeticsProfileQueryKey
} from '@/features/level-play/hooks/useLevelCosmeticUnlock'
import { useUserProgress } from '@/shared/contexts/UserProgress'
import { useOptimizedRecord } from '@/shared/hooks/useOptimizedQuery'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/Button'

import { CosmeticUnlockModal } from './CosmeticUnlockModal'
import { HUD } from './HUD'

const TEXT_VARIANTS = [
  {
    score: 0,
    icon: '/icons/face-sad.svg',
    title: 'Uma pena!',
    description: 'Você não concluiu nenhum desafio deste nível'
  },
  {
    score: 0.4,
    icon: '/icons/face-quase.svg',
    title: 'Quase lá!',
    description: 'Você concluiu alguns desafios deste nível'
  },
  {
    score: 0.7,
    icon: '/icons/face-party.svg',
    title: 'Boaa!',
    description: 'Você concluiu a maioria dos desafios deste nível'
  },
  {
    score: 1,
    icon: '/icons/face-parabens.svg',
    title: 'Parabéns!',
    description: 'Você concluiu todos os desafios deste nível'
  }
] as const

const SUMMARY_STATE_COPY = {
  loading: {
    title: 'Preparando seu próximo passo',
    description: 'Estamos organizando a continuidade da sua jornada.'
  },
  nextLevel: {
    title: 'Seu próximo passo já está pronto',
    description: 'Continue quando quiser para avançar na jornada.'
  },
  journeyFinished: {
    title: 'Fase concluída com sucesso',
    description:
      'Você concluiu esta etapa e pode voltar para jornadas para escolher o que fazer a seguir.'
  },
  error: {
    title: 'Não foi possível preparar a continuidade agora',
    description:
      'Você pode voltar para jornadas e tentar continuar novamente em instantes.'
  }
} as const

export function StepSummary() {
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
  const [progressRefreshStatus, setProgressRefreshStatus] = useState<
    'loading' | 'success' | 'error'
  >('loading')
  const [isJourneysRefetchPending, setIsJourneysRefetchPending] =
    useState(false)
  const [modalCosmetic, setModalCosmetic] =
    useState<LevelCosmeticEntry | null>(null)
  const unlockModalShownRef = useRef(false)
  const router = useAppRouter()
  const queryClient = useQueryClient()

  const { progress, refetchProgress } = useUserProgress()
  const { level, levelProgress, challenges, journeyAllowWrongAnswers } =
    useLevelContext()
  const {
    journeys,
    isLoading: isJourneysLoading,
    isError: isJourneysError,
    refetch: refetchJourneys,
    getLevelByUid
  } = useJourneysStore()

  const exitConfig = getLevelSummaryExitConfig()
  const { setActiveJourneyId, clearActiveJourney } = useActiveJourney()

  const {
    record: nextLevel,
    isLoading: isNextLevelLoading,
    isFetching: isNextLevelFetching,
    isError: isNextLevelError
  } = useOptimizedRecord<Level>({
    path: `/user/levels/${level?.uid}/next`
  })

  const currentJourney = useMemo(
    () => journeys.find(journey => journey.id === level?.journey?.id) ?? null,
    [journeys, level?.journey?.id]
  )

  const fallbackNextLevelUid = useMemo(
    () =>
      findFallbackNextLevelUid({
        currentLevelUid: level?.uid ?? null,
        journey: currentJourney,
        finishedLevelIds: progress?.finished_levels ?? []
      }),
    [currentJourney, level?.uid, progress?.finished_levels]
  )

  const nextAction = useMemo(
    () =>
      resolveCompletionNextAction({
        currentLevelUid: level?.uid ?? null,
        apiNextLevelUid: nextLevel?.uid ?? null,
        fallbackNextLevelUid,
        isProgressRefreshPending: progressRefreshStatus === 'loading',
        didProgressRefreshFail: progressRefreshStatus === 'error',
        isNextLevelLookupPending:
          !nextLevel?.uid && (isNextLevelLoading || isNextLevelFetching),
        didNextLevelLookupFail: isNextLevelError,
        isJourneyDataPending: isJourneysLoading || isJourneysRefetchPending,
        didJourneyDataFail: isJourneysError,
        hasJourneyData: Boolean(currentJourney)
      }),
    [
      currentJourney,
      fallbackNextLevelUid,
      isJourneysError,
      isJourneysLoading,
      isJourneysRefetchPending,
      isNextLevelError,
      isNextLevelFetching,
      isNextLevelLoading,
      level?.uid,
      nextLevel?.uid,
      progressRefreshStatus
    ]
  )

  const isNextLevelInDifferentStage = useMemo(() => {
    if (nextAction.kind !== 'next-level') return false
    const currentStageId = getLevelByUid(level?.uid ?? '')?.stage?.id
    const nextStageId = getLevelByUid(nextAction.targetLevelUid)?.stage?.id
    if (!currentStageId || !nextStageId) return false
    return currentStageId !== nextStageId
  }, [nextAction, level?.uid, getLevelByUid])

  const summaryMessage = useMemo(() => {
    if (nextAction.kind === 'loading') return SUMMARY_STATE_COPY.loading
    if (nextAction.kind === 'next-level') return SUMMARY_STATE_COPY.nextLevel
    if (nextAction.kind === 'error') return SUMMARY_STATE_COPY.error
    return SUMMARY_STATE_COPY.journeyFinished
  }, [nextAction.kind])

  const levelPercentCorrectAnswer = useMemo(() => {
    if (
      !Array.isArray(levelProgress.challenges) ||
      levelProgress.challenges.length === 0
    )
      return 0
    const correct = levelProgress.challenges.filter(c =>
      Boolean(c._correctAnswer)
    ).length
    const pct = correct / levelProgress.challenges.length
    return Math.max(0, Math.min(1, pct))
  }, [levelProgress])

  const text = useMemo(() => {
    const variant =
      TEXT_VARIANTS.find(v => levelPercentCorrectAnswer <= v.score) ??
      TEXT_VARIANTS[TEXT_VARIANTS.length - 1]
    return variant
  }, [levelPercentCorrectAnswer])

  // "Sair" (e o X do header): volta para a tela da jornada que acabou de ser
  // jogada — mantém a jornada ativa para a listagem abrir direto no mapa dela.
  const handleExitToJourney = useCallback(() => {
    const journeyId = level?.journey?.id
    if (journeyId) setActiveJourneyId(journeyId)
    router.push(exitConfig.destination)
  }, [exitConfig.destination, level?.journey?.id, router, setActiveJourneyId])

  // "Voltar para jornadas": limpa a jornada ativa para cair na listagem.
  const handleBackToJourneysList = useCallback(() => {
    clearActiveJourney()
    router.push(exitConfig.destination)
  }, [clearActiveJourney, exitConfig.destination, router])

  const handleNextLevel = useCallback(() => {
    if (nextAction.kind !== 'next-level') return
    setIsRedirecting(true)
    router.push(`/niveis/${nextAction.targetLevelUid}`)
  }, [nextAction, router])

  const handleDownloadLevelAnswers = useCallback(async () => {
    const journeyUid = level?.journey?.uid
    const levelUid = level?.uid
    if (!journeyUid || !levelUid || isDownloadingPdf) return

    try {
      setIsDownloadingPdf(true)
      const data = await fetchLevelAnswersExport(journeyUid, levelUid)
      await generateJourneyAnswersPdf(data)
    } catch (error) {
      if (isAxiosError<{ error?: string }>(error)) {
        toast.error(
          error.response?.data?.error ||
            'Nao foi possivel gerar o PDF deste nivel.'
        )
      } else {
        toast.error('Nao foi possivel gerar o PDF deste nivel.')
      }
    } finally {
      setIsDownloadingPdf(false)
    }
  }, [level?.journey?.uid, level?.uid, isDownloadingPdf])

  const showAnswersList =
    Boolean(journeyAllowWrongAnswers) &&
    Array.isArray(challenges) &&
    challenges.length > 0

  useEffect(() => {
    let cancelled = false

    const refreshProgress = async () => {
      setProgressRefreshStatus('loading')
      try {
        const previousCatalog =
          queryClient.getQueryData<CosmeticsProfileQueryData>(
            userCosmeticsProfileQueryKey
          )?.cosmetics ?? []

        await refetchProgress()
        const nextCatalog = (
          await queryClient.fetchQuery({
            queryKey: userCosmeticsProfileQueryKey,
            queryFn: fetchUserCosmeticsProfile
          })
        ).cosmetics ?? []

        if (!cancelled) {
          const newlyUnlockedCosmetic = resolveNewlyUnlockedCosmetic({
            levelId: level?.id ?? null,
            previousCosmetics: previousCatalog,
            nextCosmetics: nextCatalog
          })

          if (!unlockModalShownRef.current && newlyUnlockedCosmetic) {
            unlockModalShownRef.current = true
            setModalCosmetic(newlyUnlockedCosmetic)
          }

          setProgressRefreshStatus('success')
          setIsJourneysRefetchPending(true)
          try {
            await refetchJourneys()
          } finally {
            if (!cancelled) setIsJourneysRefetchPending(false)
          }
        }
      } catch {
        if (!cancelled) {
          setProgressRefreshStatus('error')
        }
      }
    }

    void refreshProgress()

    return () => {
      cancelled = true
    }
  }, [level?.id, queryClient, refetchProgress, refetchJourneys])

  return (
    <>
      {modalCosmetic && (
        <CosmeticUnlockModal
          cosmetic={modalCosmetic}
          onConfirm={() => setModalCosmetic(null)}
        />
      )}
      <HUD.Root>
      <HUD.Card>
        <HUD.Header onClose={handleExitToJourney}>{text.title}</HUD.Header>

        <HUD.Body className="row-span-2">
          <div className="flex flex-col items-center justify-center h-full">
            <div className="flex flex-col items-center rounded-2xl bg-jacaranda/20 p-6 w-full">
              <span className="mb-4 text-center text-sm font-semibold uppercase tracking-wider text-foreground">
                Você ganhou
              </span>

              <div className="grid min-w-[300px] grid-cols-2 gap-4">
                <div className="border-border text-center md:border-r">
                  <div className="flex items-center justify-center gap-2 text-xl font-bold text-foreground">
                    <img
                      src="/icons/star.svg"
                      className="h-6 w-6"
                      alt="Pontos"
                    />
                    <span>
                      {levelProgress.rewards.acquiredPoints}
                      {journeyAllowWrongAnswers && (
                        <>
                          <span className="mx-1">/</span>
                          <span className="text-lg text-gray-900">
                            {levelProgress.rewards.totalPoints}
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                  <span className="block text-xs font-bold uppercase text-gray-900">
                    Pontos
                  </span>
                </div>

                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-xl font-bold text-foreground">
                    <img
                      src="/icons/coin.svg"
                      className="h-6 w-6"
                      alt="Moedas"
                    />
                    <span>
                      {levelProgress.rewards.acquiredCoins}
                      {journeyAllowWrongAnswers && (
                        <>
                          <span className="mx-1">/</span>
                          <span className="text-lg text-gray-900">
                            {levelProgress.rewards.totalCoins}
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                  <span className="block text-xs font-bold uppercase text-gray-900">
                    Moedas
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleDownloadLevelAnswers}
                disabled={isDownloadingPdf}
                className="mt-4 text-[12px] font-bold uppercase text-primary underline underline-offset-4 decoration-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDownloadingPdf ? 'Gerando PDF...' : 'Baixar respostas'}
              </button>

              {/* Lista de acertos/erros por desafio */}
              {showAnswersList && (
                <>
                  <span className="mt-10 mb-2 block text-sm font-semibold text-foreground">
                    Confira os seus acertos!
                  </span>

                  <div className="mb-8 flex max-h-[175px] w-full flex-col gap-1 overflow-y-auto">
                    {levelProgress.challenges.map(challenge => {
                      const ok = Boolean(challenge._correctAnswer)
                      const name = challenges.find(
                        c => c.id === challenge.id
                      )?.name
                      return (
                        <div
                          key={challenge.uid}
                          className="flex w-full items-center justify-between rounded-md border border-border bg-muted p-2 pl-3"
                        >
                          <span className="text-sm font-bold text-foreground">
                            {name}
                          </span>
                          <span
                            aria-label={ok ? 'Acertou' : 'Errou'}
                            className={cn(
                              'size-5 rounded-md border-4 border-jacaranda',
                              {
                                'bg-success': ok,
                                'bg-red-500': !ok
                              }
                            )}
                          />
                        </div>
                      )
                    })}
                  </div>
                </>
              )}

              <div className="mt-6 w-full rounded-2xl bg-white/70 px-4 py-4 text-center">
                <span className="block text-sm font-bold text-foreground">
                  {summaryMessage.title}
                </span>
                <span className="mt-1 block text-sm text-gray-700">
                  {summaryMessage.description}
                </span>
              </div>
            </div>
          </div>
        </HUD.Body>

        <HUD.Footer>
          {!isRedirecting && (
            <Button onClick={handleExitToJourney}>Sair</Button>
          )}

          {nextAction.kind === 'next-level' && (
            <Button
              onClick={handleNextLevel}
              variant="success"
              className="flex-1"
              disabled={isRedirecting}
            >
              {isRedirecting ? (
                <>
                  <svg
                    className="mr-2 inline h-4 w-4 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                  Carregando próximo nível
                </>
              ) : isNextLevelInDifferentStage ? (
                <>Próxima fase</>
              ) : (
                <>Próximo nível</>
              )}
            </Button>
          )}

          {nextAction.kind === 'journey-finished' && (
            <Button
              onClick={handleBackToJourneysList}
              variant="success"
              className="flex-1"
            >
              Voltar para jornadas
            </Button>
          )}

          {nextAction.kind === 'error' && (
            <Button
              onClick={handleBackToJourneysList}
              variant="success"
              className="flex-1"
            >
              Voltar para jornadas
            </Button>
          )}

          {nextAction.kind === 'loading' && (
            <Button variant="success" className="flex-1" disabled>
              Preparando próximo passo...
            </Button>
          )}
        </HUD.Footer>
      </HUD.Card>
    </HUD.Root>
    </>
  )
}
